<template>
    <div>
        <Loading v-if="loading" />
        <div class="phone-resolver" v-else>
            <div class="box box--default phone-resolver__box">
                <template v-if="customers.length">
                    <div class="phone-resolver__title">{{ $t("customerByPhoneResolver.customersFound") }}</div>
                    <div class="phone-resolver__title">
                        {{ $t("customerByPhoneResolver.multipleTitle") }}
                    </div>
                    <div class="phone-resolver__message">
                        {{ $t("customerByPhoneResolver.selectCustomer") }} {{ decodedPhone }}.
                        {{ $t("customerByPhoneResolver.selectMessage", { phone: decodedPhone }) }}
                    </div>

                    <button v-for="customer in customers" :key="customer.id" type="button"
                        class="phone-resolver__customer" @click="openCustomer(customer.id)">
                        <span class="phone-resolver__name">{{ customerName(customer) }}</span>
                        <span class="phone-resolver__team">
                            {{ customer.team && customer.team.name ? customer.team.name : "" }}
                        </span>
                    </button>
                </template>

                <template v-else>
                    <div class="phone-resolver__title">{{ title }}</div>
                    <div class="phone-resolver__message">{{ message }}</div>

                    <div class="phone-resolver__actions">
                        <button type="button" class="button button--default" @click="openCustomers">
                            {{ $t("customerByPhoneResolver.customersButton") }}
                        </button>
                        <button type="button" class="button button--default" @click="openDashboard">
                            {{ $t("customerByPhoneResolver.dashboardButton") }}
                        </button>
                    </div>
                </template>
            </div>
        </div>
    </div> 
</template>
<script setup>
import api from "@/api";
import { useRoute, useRouter } from "vue-router";
import {
    computed,
    ref,
    watch,
} from "vue";
import { useI18n } from "vue-i18n";
import Loading from "@/partials/Loading"; 

const route = useRoute();
const router = useRouter();
const { t } = useI18n(); 
let resolveRequestId = 0;

//data
const loading = ref(false);
const customers = ref([]);
const titleKey = ref('resolvingTitle');
const messageKey = ref('resolvingMessage');

//computed 
const decodedPhone = computed(() => {
    const phone = route.params.phone;
    return Array.isArray(phone) ? phone[0] || "" : phone || "";
});
const title = computed(() => t(`customerByPhoneResolver.${titleKey.value}`));
const message = computed(() => t(`customerByPhoneResolver.${messageKey.value}`));



//methods
const resolveCustomer = async () => {
    const requestId = ++resolveRequestId;
    const phone = decodedPhone.value;

    if (!phone) {
        showNotFound();
        return;
    }

    loading.value = true;
    customers.value = [];
    titleKey.value = 'resolvingTitle';
    messageKey.value = 'resolvingMessage'; 
    
    try {
        const response = await api({ requiresAuth: true }).get("/v1/customers/find-by-phone", {
            params: {
                phone
            }
        });

        if (requestId !== resolveRequestId) {
            return;
        }

        if (response.data && response.data.customer_id) {
            openCustomer(response.data.customer_id);
            return;
        }

        showNotFound();
    } catch (error) {
        if (requestId !== resolveRequestId) {
            return;
        }

        if (error.response && error.response.status === 409) {
            customers.value = extractCustomers(error.response.data.customers);
            titleKey.value = 'multipleTitle';
            messageKey.value = 'selectMessage';
        } else if (error.response && error.response.status === 404) {
            showNotFound();
        } else {
            titleKey.value = 'errorTitle';
            messageKey.value = 'errorMessage';
        }
    } finally {
        if (requestId === resolveRequestId) {
            loading.value = false;
        }
    }
}

const extractCustomers = (customers) => {
    if (Array.isArray(customers)) {
        return customers
    }

    if (customers && Array.isArray(customers.data)) {
        return customers.data
    }

    return [];
}


const customerName = (customer) => {
    if (!customer) { return ''; }
    else {
        return customer.full_name
            || customer.company_name
            || `${customer.first_name || ""} ${customer.last_name || ""}`.trim()
            || t("customerByPhoneResolver.customerFallback", { id: customer.id });
    }
}

const openCustomer = (customerId) => {
    router.push(`/account-details/customer/${customerId}`);
}

const openCustomers = () => {
    router.push('/customers');
}

const openDashboard = () => {
    router.push('/dashboard');
}

const showNotFound = () => {
    titleKey.value = 'notFoundTitle';
    messageKey.value = 'notFoundMessage'; 
} 

//watcher
watch(
    () => route.params.phone,
    (newPhone) => { if (newPhone) resolveCustomer(); },
    { immediate: true }  
);
</script>

<style lang="scss" scoped>
.phone-resolver {
    min-height: 420px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;

    &__box {
        width: 100%;
        max-width: 520px;
        min-height: 180px;
        padding: 28px;
    }

    &__title {
        font-size: 20px;
        font-weight: 700;
        color: #354052;
        margin-bottom: 10px;
    }

    &__message {
        font-size: 14px;
        color: #7f8fa4;
        margin-bottom: 20px;
    }

    &__customer {
        width: 100%;
        border: 1px solid rgba(127, 143, 164, 0.3);
        background: #fff;
        border-radius: 4px;
        padding: 14px 16px;
        margin-bottom: 10px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        text-align: left;
    }

    &__name {
        color: #354052;
        font-weight: 700;
    }

    &__team {
        color: #7f8fa4;
        font-size: 13px;
        margin-left: 12px;
    }

    &__actions {
        display: flex;
        gap: 12px;
    }
}
</style>
